<script setup>
import { ref, computed, onMounted } from "vue";
import Swal from "sweetalert2";
import api from "../../api/axios";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";

const BACKEND_ROOT = api.defaults.baseURL.replace(/\/api\/?$/, "");

const bookings = ref([]);
const providers = ref([]);
const contacts = ref([]);
const cargando = ref(true);
const guardando = ref(false);
const search = ref("");

const showModal = ref(false);
const isEdit = ref(false);
const serviciosDelProveedor = ref([]);

const form = ref({
  id: null, contactId: "", providerId: "", providerServiceId: "", productName: "",
  startDate: "", endDate: "", adults: 1, children: 0, totalPrice: "", notes: ""
});

const METODOS_PAGO = [
  "Efectivo", "Transferencia", "Consignación", "Nequi", "Daviplata",
  "Tarjeta débito", "Tarjeta crédito", "PSE", "Bold", "Otro"
];

function hoyISO() {
  return new Date().toISOString().substring(0, 10);
}

const showPagos = ref(false);
const guardandoPago = ref(false);
const bookingPagos = ref(null);
const pagos = ref([]);
const balance = ref(null);
const nuevoPago = ref({ amount: "", method: "", metodoOtro: "", paidAt: hoyISO(), notes: "" });

const saldoActual = computed(() => Number(balance.value?.balance ?? 0));
const totalAbonado = computed(() => Number(balance.value?.totalPaid ?? 0));
const totalReserva = computed(() => Number(balance.value?.totalPrice ?? 0));
const pctPagado = computed(() => {
  if (!totalReserva.value) return 0;
  return Math.min(100, Math.round((totalAbonado.value / totalReserva.value) * 100));
});
const reservaPagada = computed(() => totalReserva.value > 0 && saldoActual.value <= 0);

async function getBookings() {
  const res = await api.get("/bookings", { params: { search: search.value || undefined } });
  bookings.value = res.data.bookings;
}

async function getProviders() {
  const res = await api.get("/providers");
  providers.value = res.data.providers;
}

async function getContacts() {
  const res = await api.get("/crm/contacts", { params: { limit: 500 } });
  contacts.value = res.data.contacts;
}

async function onProviderChange() {
  form.value.providerServiceId = "";
  serviciosDelProveedor.value = [];
  if (!form.value.providerId) return;

  const res = await api.get(`/providers/${form.value.providerId}`);
  serviciosDelProveedor.value = res.data.provider.services;
}

function onServiceChange() {
  const servicio = serviciosDelProveedor.value.find(s => s.id === Number(form.value.providerServiceId));
  if (servicio) {
    form.value.productName = servicio.name;
    if (!form.value.totalPrice) form.value.totalPrice = servicio.price;
  }
}

function resetForm() {
  form.value = {
    id: null, contactId: "", providerId: "", providerServiceId: "", productName: "",
    startDate: "", endDate: "", adults: 1, children: 0, totalPrice: "", notes: ""
  };
  serviciosDelProveedor.value = [];
}

function abrirCrear() {
  resetForm();
  isEdit.value = false;
  showModal.value = true;
}

async function abrirEditar(booking) {
  form.value = {
    id: booking.id,
    contactId: booking.contact_id || "",
    providerId: booking.provider_id || "",
    providerServiceId: booking.provider_service_id || "",
    productName: booking.product_name,
    startDate: booking.start_date ? booking.start_date.substring(0, 10) : "",
    endDate: booking.end_date ? booking.end_date.substring(0, 10) : "",
    adults: booking.adults,
    children: booking.children,
    totalPrice: booking.total_price,
    notes: booking.notes || ""
  };

  if (form.value.providerId) {
    const res = await api.get(`/providers/${form.value.providerId}`);
    serviciosDelProveedor.value = res.data.provider.services;
  }

  isEdit.value = true;
  showModal.value = true;
}

async function guardarBooking() {
  if (!form.value.productName) {
    Swal.fire({ icon: "warning", title: "El producto/servicio vendido es obligatorio" });
    return;
  }

  guardando.value = true;

  try {
    if (isEdit.value) {
      await api.put(`/bookings/${form.value.id}`, form.value);
    } else {
      await api.post("/bookings", form.value);
    }

    showModal.value = false;
    await getBookings();

    Swal.fire({ icon: "success", title: isEdit.value ? "Reserva actualizada" : "Reserva creada", timer: 1500, showConfirmButton: false });

  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo guardar la reserva", text: err.response?.data?.error || "Error de conexión." });
  } finally {
    guardando.value = false;
  }
}

async function eliminarBooking(booking) {
  const { isConfirmed } = await Swal.fire({
    icon: "warning",
    title: "¿Eliminar reserva?",
    text: "Esta acción no se puede deshacer.",
    showCancelButton: true,
    confirmButtonText: "Sí, eliminar",
    cancelButtonText: "Cancelar",
    confirmButtonColor: "#f5365c"
  });

  if (!isConfirmed) return;

  try {
    await api.delete(`/bookings/${booking.id}`);
    await getBookings();
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo eliminar la reserva",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    });
  }
}

function resetNuevoPago() {
  nuevoPago.value = { amount: "", method: "", metodoOtro: "", paidAt: hoyISO(), notes: "" };
}

async function abrirPagos(booking) {
  bookingPagos.value = booking;
  const res = await api.get(`/bookings/${booking.id}/payments`);
  pagos.value = res.data.payments;
  balance.value = res.data.balance;
  resetNuevoPago();
  showPagos.value = true;
}

function abonarSaldo() {
  if (saldoActual.value > 0) nuevoPago.value.amount = saldoActual.value;
}

async function agregarPago() {
  if (!nuevoPago.value.amount || Number(nuevoPago.value.amount) <= 0) {
    Swal.fire({ icon: "warning", title: "El monto del abono debe ser mayor a cero" });
    return;
  }

  // "Otro" deja escribir un método a mano; el resto usa el valor de la lista.
  const metodo = nuevoPago.value.method === "Otro"
    ? (nuevoPago.value.metodoOtro || "").trim()
    : nuevoPago.value.method;

  guardandoPago.value = true;
  try {
    const res = await api.post(`/bookings/${bookingPagos.value.id}/payments`, {
      amount: nuevoPago.value.amount,
      method: metodo || null,
      paidAt: nuevoPago.value.paidAt || null,
      notes: (nuevoPago.value.notes || "").trim() || null
    });
    pagos.value.push(res.data.payment);
    balance.value = res.data.balance;
    resetNuevoPago();
    await getBookings();
  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo registrar el abono", text: err.response?.data?.error || "Error de conexión." });
  } finally {
    guardandoPago.value = false;
  }
}

async function descargarFactura(booking) {
  try {
    const res = await api.post(`/bookings/${booking.id}/invoice`);
    window.open(`${BACKEND_ROOT}${res.data.invoice.pdf_url}`, "_blank");
  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo generar la factura", text: err.response?.data?.error || "Error de conexión." });
  }
}

function formatCOP(valor) {
  return Number(valor || 0).toLocaleString("es-CO", { style: "currency", currency: "COP", minimumFractionDigits: 0 });
}

function estadoBadge(status) {
  return { confirmada: "bg-success", cancelada: "bg-danger", completada: "bg-gradient-secondary" }[status] || "bg-gradient-secondary";
}

const totalPaxNuevoPago = computed(() => Number(form.value.adults || 0) + Number(form.value.children || 0));

function totalPax(booking) {
  return Number(booking.adults || 0) + Number(booking.children || 0);
}

function porPersona(booking) {
  const pax = totalPax(booking);
  return pax > 0 ? formatCOP(Number(booking.total_price) / pax) : formatCOP(booking.total_price);
}

onMounted(async () => {
  cargando.value = true;
  await Promise.all([getBookings(), getProviders(), getContacts()]);
  cargando.value = false;
});
</script>

<template>
  <div class="container-fluid py-4">

    <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
      <h4 class="mb-0" style="color: white;">
        <i class="bi bi-journal-check me-2"></i>
        Reservas
      </h4>

      <argon-button color="success" @click="abrirCrear">
        <i class="bi bi-plus-circle me-1"></i>
        Nueva reserva
      </argon-button>
    </div>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else class="card">
      <div class="card-body table-responsive">
        <table class="table table-striped align-middle">
          <thead>
            <tr>
              <th>Cliente</th>
              <th>Producto</th>
              <th>Proveedor</th>
              <th>Vendedor</th>
              <th>Fechas</th>
              <th>Pax</th>
              <th>Total</th>
              <th>Pagado</th>
              <th>Saldo</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in bookings" :key="b.id">
              <td>{{ b.contact_name || "—" }}</td>
              <td>{{ b.product_name }}</td>
              <td>{{ b.provider_name || "—" }}</td>
              <td>{{ b.seller_name || "—" }}</td>
              <td>
                <span v-if="b.start_date">{{ new Date(b.start_date).toLocaleDateString("es-CO") }}</span>
                <span v-if="b.end_date"> - {{ new Date(b.end_date).toLocaleDateString("es-CO") }}</span>
              </td>
              <td>{{ totalPax(b) }}</td>
              <td>{{ formatCOP(b.total_price) }} <div class="text-muted small">{{ porPersona(b) }}/persona</div></td>
              <td>{{ formatCOP(b.total_paid) }}</td>
              <td :class="Number(b.total_price) - Number(b.total_paid) > 0 ? 'text-danger' : 'text-success'">
                {{ formatCOP(Number(b.total_price) - Number(b.total_paid)) }}
              </td>
              <td><span class="badge" :class="estadoBadge(b.status)">{{ b.status }}</span></td>
              <td class="d-flex gap-1">
                <button class="btn btn-sm btn-outline-primary" title="Abonos" @click="abrirPagos(b)"><i class="bi bi-cash-coin"></i></button>
                <button class="btn btn-sm btn-outline-success" title="Descargar factura" @click="descargarFactura(b)"><i class="bi bi-file-earmark-pdf"></i></button>
                <button class="btn btn-sm btn-outline-secondary" @click="abrirEditar(b)"><i class="bi bi-pencil"></i></button>
                <button class="btn btn-sm btn-outline-danger" @click="eliminarBooking(b)"><i class="bi bi-trash"></i></button>
              </td>
            </tr>
            <tr v-if="bookings.length === 0">
              <td colspan="11" class="text-center text-muted py-4">Todavía no hay reservas registradas.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- MODAL CREAR/EDITAR -->
    <div v-if="showModal" class="modal fade show d-block">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5>{{ isEdit ? "Editar reserva" : "Nueva reserva" }}</h5>
            <button class="btn-close" @click="showModal = false"></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label>Cliente</label>
                <select v-model="form.contactId" class="form-control">
                  <option value="">Sin cliente asociado</option>
                  <option v-for="c in contacts" :key="c.id" :value="c.id">{{ c.name }} {{ c.last_name || "" }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label>Proveedor</label>
                <select v-model="form.providerId" class="form-control" @change="onProviderChange">
                  <option value="">Sin proveedor</option>
                  <option v-for="p in providers" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </div>

              <div class="col-md-6">
                <label>Servicio del proveedor</label>
                <select v-model="form.providerServiceId" class="form-control" @change="onServiceChange" :disabled="!serviciosDelProveedor.length">
                  <option value="">Seleccionar servicio (opcional)</option>
                  <option v-for="s in serviciosDelProveedor" :key="s.id" :value="s.id">{{ s.name }} — {{ formatCOP(s.price) }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label>Producto / servicio vendido</label>
                <argon-input v-model="form.productName" placeholder="Ej. Paquete todo incluido 5 noches" />
              </div>

              <div class="col-md-3">
                <label>Fecha inicio</label>
                <input v-model="form.startDate" type="date" class="form-control" />
              </div>
              <div class="col-md-3">
                <label>Fecha fin</label>
                <input v-model="form.endDate" type="date" class="form-control" />
              </div>
              <div class="col-md-3">
                <label>Adultos</label>
                <argon-input v-model="form.adults" type="number" />
              </div>
              <div class="col-md-3">
                <label>Niños</label>
                <argon-input v-model="form.children" type="number" />
              </div>

              <div class="col-md-6">
                <label>Precio total</label>
                <argon-input v-model="form.totalPrice" type="number" />
              </div>
              <div class="col-md-6">
                <label>Valor por persona (informativo)</label>
                <input class="form-control" disabled :value="formatCOP((form.totalPrice || 0) / (totalPaxNuevoPago || 1))" />
              </div>

              <div class="col-12">
                <label>Notas</label>
                <textarea v-model="form.notes" class="form-control" rows="2"></textarea>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showModal = false">Cancelar</button>
            <argon-button color="success" :disabled="guardando" @click="guardarBooking">Guardar</argon-button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showModal" class="modal-backdrop fade show"></div>

    <!-- MODAL PAGOS/ABONOS -->
    <div v-if="showPagos" class="modal fade show d-block">
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <div>
              <h5 class="mb-0"><i class="bi bi-cash-coin me-2 text-success"></i>Abonos</h5>
              <small class="text-muted">{{ bookingPagos?.product_name }}<span v-if="bookingPagos?.contact_name"> · {{ bookingPagos.contact_name }}</span></small>
            </div>
            <button class="btn-close" @click="showPagos = false"></button>
          </div>

          <div class="modal-body">
            <!-- Resumen de saldo -->
            <div v-if="balance" class="card bg-gray-100 border-0 mb-4">
              <div class="card-body py-3">
                <div class="row text-center g-2">
                  <div class="col-4">
                    <div class="text-xs text-uppercase text-muted fw-bold">Total</div>
                    <div class="h6 mb-0">{{ formatCOP(totalReserva) }}</div>
                  </div>
                  <div class="col-4 border-start border-end">
                    <div class="text-xs text-uppercase text-muted fw-bold">Abonado</div>
                    <div class="h6 mb-0 text-success">{{ formatCOP(totalAbonado) }}</div>
                  </div>
                  <div class="col-4">
                    <div class="text-xs text-uppercase text-muted fw-bold">Saldo</div>
                    <div class="h6 mb-0" :class="saldoActual > 0 ? 'text-danger' : 'text-success'">{{ formatCOP(saldoActual) }}</div>
                  </div>
                </div>
                <div class="progress mt-3" style="height: 8px;">
                  <div class="progress-bar bg-success" role="progressbar" :style="{ width: pctPagado + '%' }"></div>
                </div>
                <div class="text-end text-xs text-muted mt-1">{{ pctPagado }}% pagado</div>
              </div>
            </div>

            <!-- Historial de abonos -->
            <h6 class="text-sm text-uppercase text-muted mb-2">Historial de abonos</h6>
            <div class="table-responsive mb-4">
              <table class="table table-sm align-middle mb-0">
                <thead>
                  <tr class="text-xs text-uppercase text-muted">
                    <th class="ps-2">Fecha</th>
                    <th>Método</th>
                    <th>Notas</th>
                    <th class="text-end pe-2">Monto</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="p in pagos" :key="p.id">
                    <td class="ps-2">{{ new Date(p.paid_at).toLocaleDateString("es-CO") }}</td>
                    <td><span class="badge bg-gradient-secondary">{{ p.method || "Sin método" }}</span></td>
                    <td class="text-sm text-muted">{{ p.notes || "—" }}</td>
                    <td class="text-end pe-2 fw-bold">{{ formatCOP(p.amount) }}</td>
                  </tr>
                  <tr v-if="pagos.length === 0">
                    <td colspan="4" class="text-center text-muted small py-3">Sin abonos registrados todavía.</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Registrar nuevo abono -->
            <div v-if="reservaPagada" class="alert alert-success d-flex align-items-center mb-0">
              <i class="bi bi-check-circle-fill me-2"></i>
              Esta reserva ya está pagada en su totalidad.
            </div>

            <div v-else class="card border">
              <div class="card-body">
                <h6 class="text-sm text-uppercase text-muted mb-3">Registrar abono</h6>
                <div class="row g-3">
                  <div class="col-md-6">
                    <label class="form-label text-xs">Monto</label>
                    <div class="input-group">
                      <argon-input v-model="nuevoPago.amount" type="number" placeholder="0" class="mb-0" />
                      <button class="btn btn-outline-success mb-0 text-nowrap" type="button" @click="abonarSaldo" :disabled="saldoActual <= 0" title="Usar el saldo pendiente">Saldo</button>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <label class="form-label text-xs">Fecha</label>
                    <input v-model="nuevoPago.paidAt" type="date" class="form-control" />
                  </div>

                  <div class="col-md-6">
                    <label class="form-label text-xs">Método de pago</label>
                    <select v-model="nuevoPago.method" class="form-control">
                      <option value="">Seleccionar método</option>
                      <option v-for="m in METODOS_PAGO" :key="m" :value="m">{{ m }}</option>
                    </select>
                  </div>
                  <div class="col-md-6" v-if="nuevoPago.method === 'Otro'">
                    <label class="form-label text-xs">¿Cuál método?</label>
                    <argon-input v-model="nuevoPago.metodoOtro" placeholder="Especificar método" class="mb-0" />
                  </div>

                  <div class="col-12">
                    <label class="form-label text-xs">Notas (opcional)</label>
                    <argon-input v-model="nuevoPago.notes" placeholder="Ej. abono inicial, pago de saldo..." class="mb-0" />
                  </div>

                  <div class="col-12 d-flex justify-content-end">
                    <argon-button color="success" :disabled="guardandoPago" @click="agregarPago">
                      <i class="bi bi-plus-circle me-1"></i>
                      {{ guardandoPago ? "Guardando..." : "Agregar abono" }}
                    </argon-button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-secondary" @click="showPagos = false">Cerrar</button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showPagos" class="modal-backdrop fade show"></div>

  </div>
</template>
